// ============================================================================
// FrontOfficeClosingScreen — PMS → Front Office Closing (PMS Phase 2 slice 7d)
// ============================================================================
// The desk's day: every receipt and refund it took (advances at check-in,
// interim advances, final settlements), the stays that checked out and how each
// bill was settled, cash paid out of the drawer, and the totals by payment
// method. Closing counts the cash and records where it went (Main Cashier, bank,
// float) and what each non-cash channel confirmed; the Main Cashier verifies; a
// reopen needs a reason. A closed day locks its Front Office receipts.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell } from 'components/enterprise-shell';
import { usePropertyScopeItems } from 'hooks/api/usePropertyScope';
import ReportHeader, { ReportBody, type ReportHeaderItem } from 'components/reports/ReportHeader';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption } from 'components/ui';
import { foClosingService } from 'api/services/foClosingService';
import { propertyService } from 'api/services/propertyService';
import notificationService from 'services/notificationService';
import { blobErrorMessage, reserveTab, showPdf } from 'utils/reportFile';
import { formatCurrency } from 'utils/currencyFormat';
import { useDefaultProperty } from 'hooks/api/useDefaultProperty';
import { usePropertyLabel } from 'hooks/api/usePropertyLabel';
import FoCloseModal from 'components/modals/modules/pms-module/FoCloseModal';
import {
  FO_RECEIPT_KIND_LABEL, FO_STATUS_LABEL,
  type FoDay, type FoPaidOutRow, type FoReceiptRow, type FoSettlementRow,
} from 'api/types/foClosing.types';
import 'components/modules/dashboard-module/dashboard.css';

const isoOf = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const shiftIso = (iso: string, days: number) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + days); return isoOf(d); };
const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const formatDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

const SETTLEMENT: Record<FoSettlementRow['status'], { label: string; badge: string }> = {
  SETTLED: { label: 'Settled', badge: 'badge-success' },
  GUEST_OWES: { label: 'Guest owes', badge: 'badge-danger' },
  CREDIT_HELD: { label: 'Credit held', badge: 'badge-warning' },
  NOT_INVOICED: { label: 'Not invoiced', badge: 'badge-secondary' },
};

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — {'view','close','verify','reopen'}. Fail-closed. */
  allowedActions?: string[];
}

type Tab = 'receipts' | 'settlements' | 'paidOut' | 'modes';

const FrontOfficeClosingScreen: React.FC<Props> = ({ tabId, allowedActions = [] }) => {
  const [propertyId, setPropertyId] = useState('');
  const { defaultPropertyId } = useDefaultProperty();
  useEffect(() => { if (!propertyId && defaultPropertyId) setPropertyId(defaultPropertyId); }, [defaultPropertyId, propertyId]);
  const [date, setDate] = useState(isoOf(new Date()));
  const [filterOpen, setFilterOpen] = useState(false);
  const propertyLabelsRef = useRef<Record<string, string>>({});
  const fetchedPropertyLabel = usePropertyLabel(propertyId);
  const propertyLabel = fetchedPropertyLabel || propertyLabelsRef.current[propertyId] || '';

  const [day, setDay] = useState<FoDay | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('receipts');
  const [closing, setClosing] = useState(false);
  const [decision, setDecision] = useState<null | 'verify' | 'reopen'>(null);
  const [decisionText, setDecisionText] = useState('');
  const [deciding, setDeciding] = useState(false);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId || !date) { setDay(null); return; }
    setLoading(true);
    setError(null);
    try {
      setDay(await foClosingService.getDay(propertyId, date));
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The day did not load.');
    } finally {
      setLoading(false);
    }
  }, [propertyId, date]);
  useEffect(() => { void load(); }, [load]);

  // The first visit lands on the property's business date (before night audit, that is still "today").
  const landedRef = useRef(false);
  useEffect(() => {
    if (landedRef.current || !day?.propertyBusinessDate) return;
    landedRef.current = true;
    const bd = day.propertyBusinessDate.slice(0, 10);
    if (bd !== date) setDate(bd);
  }, [day, date]);

  const loadProperties = useCallback(async (q: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: q || undefined, pageSize: q ? 50 : 500, includeInactive: false });
    const opts = (res.data ?? []).map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
    opts.forEach((o: SelectOption) => { propertyLabelsRef.current[o.value as string] = o.label; });
    return opts;
  }, []);

  const print = useCallback(async () => {
    if (!propertyId) return;
    const w = reserveTab();
    setPrinting(true);
    try {
      showPdf(await foClosingService.daySheet(propertyId, date), w);
    } catch (err) {
      w?.close();
      notificationService.toast.error(await blobErrorMessage(err, 'Print failed.'));
    } finally {
      setPrinting(false);
    }
  }, [propertyId, date]);

  const decide = useCallback(async () => {
    if (!day || !decision) return;
    setDeciding(true);
    try {
      const next = decision === 'verify'
        ? await foClosingService.verify(propertyId, date, decisionText.trim() || undefined)
        : await foClosingService.reopen(propertyId, date, decisionText.trim());
      notificationService.toast.success(decision === 'verify' ? 'Day verified.' : "Day reopened — its receipts can be corrected.");
      setDay(next);
      setDecision(null);
      setDecisionText('');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Not saved.');
    } finally {
      setDeciding(false);
    }
  }, [day, decision, decisionText, propertyId, date]);

  const status = day?.status ?? 'NONE';
  const canClose = allowedActions.includes('close') && (status === 'NONE' || status === 'REOPENED' || status === 'DRAFT')
    && !!day && date <= day.propertyBusinessDate.slice(0, 10);
  const canVerify = allowedActions.includes('verify') && status === 'CLOSED';
  const canReopen = allowedActions.includes('reopen') && (status === 'CLOSED' || status === 'VERIFIED');

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [
      { id: 'prev', icon: <IconSystem.chevronLeft size={16} />, label: 'Previous day', onClick: () => setDate((d) => shiftIso(d, -1)), variant: 'icon' },
      { id: 'next', icon: <IconSystem.chevronRight size={16} />, label: 'Next day', onClick: () => setDate((d) => shiftIso(d, 1)), variant: 'icon' },
      { id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh', onClick: () => { void load(); }, variant: 'icon' },
      { id: 'print', icon: <IconSystem.print size={16} />, label: printing ? 'Preparing…' : 'Day sheet', tooltip: 'Print the day sheet', onClick: () => { void print(); }, variant: 'icon' },
    ];
    if (canReopen) b.push({ id: 'reopen', icon: <IconSystem.unlock size={16} />, label: 'Reopen', onClick: () => { setDecision('reopen'); setDecisionText(''); }, variant: 'secondary' });
    if (canVerify) b.push({ id: 'verify', icon: <IconSystem.checkCircle size={16} />, label: 'Verify', onClick: () => { setDecision('verify'); setDecisionText(''); }, variant: 'secondary' });
    if (canClose) b.push({ id: 'close', icon: <IconSystem.lock size={16} />, label: 'Close day', onClick: () => setClosing(true), variant: 'primary' });
    return b;
  }, [load, print, printing, canClose, canVerify, canReopen]);

  const receiptColumns = useMemo<SyutGridColumn<FoReceiptRow & { id: string }>[]>(() => [
    { id: 'createdAt', header: 'Time', accessorKey: 'createdAt', size: 80, cell: ({ row }) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '' },
    { id: 'receiptNumber', header: 'Receipt', accessorKey: 'receiptNumber', size: 150, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.receiptNumber || '—'}</span> },
    {
      id: 'guestName', header: 'Guest / Booking', accessorKey: 'guestName', size: 200,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.bookingNumber}{row.original.roomNumbers ? ` · ${row.original.roomNumbers}` : ''}</div>
        </span>
      ),
    },
    { id: 'classification', header: 'Kind', accessorKey: 'classification', size: 150, cell: ({ row }) => FO_RECEIPT_KIND_LABEL[row.original.classification] ?? row.original.classification },
    { id: 'paymentMode', header: 'Method', accessorKey: 'paymentMode', size: 110, cell: ({ row }) => <span>{row.original.paymentMode}{row.original.isCash && <span className="badge badge-secondary" style={{ fontSize: '0.58rem', marginLeft: 4 }}>cash</span>}</span> },
    { id: 'paymentReference', header: 'Reference', accessorKey: 'paymentReference', size: 130, cell: ({ row }) => row.original.paymentReference || '—' },
    { id: 'receivedByName', header: 'By', accessorKey: 'receivedByName', size: 120, cell: ({ row }) => row.original.receivedByName || '—' },
    { id: 'amount', header: 'Amount', accessorKey: 'amount', size: 110, cell: ({ row }) => <b style={{ color: row.original.amount < 0 ? 'var(--danger, #dc2626)' : undefined }}>{formatCurrency(row.original.amount)}</b> },
  ], []);

  const settlementColumns = useMemo<SyutGridColumn<FoSettlementRow & { id: string }>[]>(() => [
    {
      id: 'guestName', header: 'Guest / Booking', accessorKey: 'guestName', size: 200,
      cell: ({ row }) => (
        <span>{row.original.guestName || '—'}
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.bookingNumber}{row.original.roomNumbers ? ` · ${row.original.roomNumbers}` : ''}</div>
        </span>
      ),
    },
    { id: 'bill', header: 'Bill', accessorKey: 'bill', size: 110, cell: ({ row }) => formatCurrency(row.original.bill) },
    {
      id: 'transferredToParty', header: 'To party', accessorKey: 'transferredToParty', size: 150,
      cell: ({ row }) => row.original.transferredToParty
        ? <span>{formatCurrency(row.original.transferredToParty)}<div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.partyNames}</div></span>
        : '—',
    },
    { id: 'advancesAdjusted', header: 'Advances', accessorKey: 'advancesAdjusted', size: 110, cell: ({ row }) => formatCurrency(row.original.advancesAdjusted) },
    {
      id: 'collectedAtCheckOut', header: 'At checkout', accessorKey: 'collectedAtCheckOut', size: 160,
      cell: ({ row }) => <span>{formatCurrency(row.original.collectedAtCheckOut)}{row.original.collectedByMode && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.collectedByMode}</div>}</span>,
    },
    { id: 'refunded', header: 'Refunded', accessorKey: 'refunded', size: 100, cell: ({ row }) => row.original.refunded ? formatCurrency(row.original.refunded) : '—' },
    { id: 'outstanding', header: 'Outstanding', accessorKey: 'outstanding', size: 110, cell: ({ row }) => <b>{formatCurrency(row.original.outstanding)}</b> },
    {
      id: 'status', header: 'Status', accessorKey: 'status', size: 120,
      cell: ({ row }) => {
        const s = SETTLEMENT[row.original.status];
        return <span className={`badge ${s.badge}`} style={{ fontSize: '0.62rem' }} title={row.original.notYetInvoiced ? `${formatCurrency(row.original.notYetInvoiced)} still to invoice` : undefined}>{s.label}</span>;
      },
    },
  ], []);

  const paidOutColumns = useMemo<SyutGridColumn<FoPaidOutRow & { id: string }>[]>(() => [
    { id: 'voucherNumber', header: 'Voucher', accessorKey: 'voucherNumber', size: 160, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.voucherNumber}</span> },
    { id: 'guestName', header: 'Guest / Booking', accessorKey: 'guestName', size: 200, cell: ({ row }) => <span>{row.original.guestName || '—'}<div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{row.original.bookingNumber}</div></span> },
    { id: 'description', header: 'For', accessorKey: 'description', size: 260 },
    { id: 'amount', header: 'Amount', accessorKey: 'amount', size: 110, cell: ({ row }) => <b>{formatCurrency(row.original.amount)}</b> },
  ], []);

  const receipts = useMemo(() => (day?.receipts ?? []).map((r) => ({ ...r, id: r.paymentId })), [day]);
  const settlements = useMemo(() => (day?.settlements ?? []).map((r) => ({ ...r, id: r.bookingId })), [day]);
  const paidOuts = useMemo(() => (day?.paidOuts ?? []).map((r) => ({ ...r, id: r.voucherId })), [day]);

  const st = FO_STATUS_LABEL[status];
  const scopeItems = usePropertyScopeItems(propertyId, date);
  const headerItems: ReportHeaderItem[] = [
    { label: 'Property', value: propertyLabel || (propertyId ? '…' : 'Select in filters'), primary: true },
    { label: 'Date', value: formatDate(date), primary: true },
    ...scopeItems,
    { label: 'Status', value: <span className={`badge ${st.badge}`} style={{ fontSize: '0.62rem' }}>{st.label}</span> as any },
    ...(day ? [
      { label: 'Received', value: formatCurrency(day.totalReceipts) },
      { label: 'Refunded', value: formatCurrency(day.totalRefunds) },
      { label: 'Expected cash', value: formatCurrency(day.expectedCash), title: `Opening ${formatCurrency(day.openingFloat)} + cash in ${formatCurrency(day.cashReceived)} − out ${formatCurrency(day.cashPaidOut)}` },
      ...(day.countedCash != null ? [
        { label: 'Counted', value: formatCurrency(day.countedCash) },
        { label: day.cashDifference < 0 ? 'Short' : day.cashDifference > 0 ? 'Over' : 'Difference', value: formatCurrency(day.cashDifference), title: day.differenceReason ?? undefined },
      ] : []),
    ] : []),
  ];

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'receipts', label: `Receipts (${receipts.length})` },
    { id: 'settlements', label: `Stays settled (${settlements.length})` },
    { id: 'paidOut', label: `Cash paid out (${paidOuts.length})` },
    { id: 'modes', label: 'By method & cash' },
  ];

  const closeSummary = day && (status === 'CLOSED' || status === 'VERIFIED' || status === 'REOPENED') ? (
    <span>
      Closed {formatDateTime(day.closedAt)} by <b>{day.closedByName}</b>
      {status === 'VERIFIED' && <> · verified {formatDateTime(day.verifiedAt)} by <b>{day.verifiedByName}</b>{day.verificationRemarks ? ` (${day.verificationRemarks})` : ''}</>}
      {status === 'REOPENED' && <> · reopened by <b>{day.reopenedByName}</b>: {day.reopenReason}</>}
    </span>
  ) : undefined;

  const footer = day ? (
    <>
      {Object.entries(day.byClassification).map(([k, v]) => (
        <span key={k}>{FO_RECEIPT_KIND_LABEL[k as keyof typeof FO_RECEIPT_KIND_LABEL] ?? k} <b>{formatCurrency(v ?? 0)}</b></span>
      ))}
      {closeSummary}
      {day.unrecordedCount > 0 && <span style={{ color: 'var(--text-muted)' }}>{day.unrecordedCount} older receipt(s) without a collection point not counted</span>}
    </>
  ) : undefined;

  const cell = { padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border-color)' } as const;
  const right = { ...cell, textAlign: 'right' as const };

  return (
    <EnterpriseScreenShell
      title="Front Office Closing"
      description="The desk's collections for the day, the stays settled, and the close: cash counted, handed over, deposited and kept; card and UPI confirmed."
      icon={<IconSystem.lock size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      filterConfig={{ onClick: () => setFilterOpen((v) => !v), isActive: filterOpen, appliedCount: 0 }}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={14} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label={<>Property <span className="text-danger">*</span></>} value={propertyId}
                  onChange={(v) => setPropertyId((v as string) || '')} isAsync loadOptions={loadProperties} defaultOptions cacheOptions={false}
                  isClearable={false} menuPortalTarget={document.body} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Date</label>
                <input type="date" className="form-input" value={date} onChange={(e) => setDate(e.target.value)} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <ReportHeader items={headerItems} />
      <div style={{ display: 'flex', gap: '0.25rem', padding: '0.35rem 0.75rem 0', flexShrink: 0 }} role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
            className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>
      <ReportBody footer={footer}>
        {!propertyId ? (
          <div style={{ color: 'var(--text-muted)', padding: '2rem', textAlign: 'center' }}>Pick a property in the filters.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger, #ef4444)', padding: '2rem', textAlign: 'center' }}>Failed to load — {error}</div>
        ) : tab === 'receipts' ? (
          <SyutGrid data={receipts} columns={receiptColumns} isLoading={loading} emptyMessage="The Front Office took no payments on this day." />
        ) : tab === 'settlements' ? (
          <SyutGrid data={settlements} columns={settlementColumns} isLoading={loading} emptyMessage="No stay checked out on this day." />
        ) : tab === 'paidOut' ? (
          <SyutGrid data={paidOuts} columns={paidOutColumns} isLoading={loading} emptyMessage="No cash was paid out at the desk." />
        ) : day && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: '1rem', padding: '0.75rem', overflow: 'auto', fontSize: '0.83rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...cell, textAlign: 'left' }}>Method</th><th style={right}>Receipts</th><th style={right}>Received</th>
                  <th style={right}>Refunded</th><th style={right}>Net</th><th style={right}>Confirmed</th><th style={right}>Difference</th>
                </tr>
              </thead>
              <tbody>
                {day.modes.map((m) => (
                  <tr key={m.paymentMode}>
                    <td style={cell}>{m.paymentMode}{m.isCash && <span className="badge badge-secondary" style={{ fontSize: '0.58rem', marginLeft: 4 }}>cash</span>}</td>
                    <td style={right}>{m.receiptCount}</td>
                    <td style={right}>{formatCurrency(m.receipts)}</td>
                    <td style={right}>{m.refunds ? formatCurrency(m.refunds) : '—'}</td>
                    <td style={right}><b>{formatCurrency(m.systemAmount)}</b></td>
                    <td style={right} title={m.confirmationRef ?? undefined}>{m.isCash ? 'counted' : m.confirmedAmount != null ? formatCurrency(m.confirmedAmount) : '—'}</td>
                    <td style={right} title={m.differenceReason ?? undefined}>{m.isCash ? '' : m.difference ? formatCurrency(m.difference) : '—'}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...cell, fontWeight: 600 }}>Total</td><td style={right} />
                  <td style={{ ...right, fontWeight: 600 }}>{formatCurrency(day.totalReceipts)}</td>
                  <td style={{ ...right, fontWeight: 600 }}>{formatCurrency(day.totalRefunds)}</td>
                  <td style={{ ...right, fontWeight: 600 }}>{formatCurrency(day.totalReceipts - day.totalRefunds)}</td><td style={right} /><td style={right} />
                </tr>
              </tbody>
            </table>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={cell}>Opening float{day.openingFromPreviousClose ? '' : ' (first close)'}</td><td style={right}>{formatCurrency(day.openingFloat)}</td></tr>
                <tr><td style={cell}>Add: cash received</td><td style={right}>{formatCurrency(day.cashReceived)}</td></tr>
                <tr><td style={cell}>Less: refunded and paid out</td><td style={right}>{formatCurrency(day.cashPaidOut)}</td></tr>
                <tr><td style={{ ...cell, fontWeight: 600 }}>Expected in the drawer</td><td style={{ ...right, fontWeight: 600 }}>{formatCurrency(day.expectedCash)}</td></tr>
                {day.countedCash != null && (
                  <>
                    <tr><td style={cell}>Counted</td><td style={right}>{formatCurrency(day.countedCash)}</td></tr>
                    <tr><td style={cell}>{day.cashDifference < 0 ? 'Short' : day.cashDifference > 0 ? 'Over' : 'Difference'}{day.differenceReason ? ` — ${day.differenceReason}` : ''}</td><td style={right}>{formatCurrency(day.cashDifference)}</td></tr>
                    <tr><td style={cell}>Handed to the Main Cashier{day.handedOverToName ? ` — ${day.handedOverToName}` : ''}{day.handedOverReference ? ` (${day.handedOverReference})` : ''}</td><td style={right}>{formatCurrency(day.handedOverAmount)}</td></tr>
                    <tr><td style={cell}>Deposited in the bank{day.depositSlipNumber ? ` — slip ${day.depositSlipNumber}` : ''}</td><td style={right}>{formatCurrency(day.depositedAmount)}</td></tr>
                    <tr><td style={cell}>Retained as float</td><td style={right}>{formatCurrency(day.retainedFloat)}</td></tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </ReportBody>

      {closing && day && (
        <FoCloseModal day={day} onClose={() => setClosing(false)} onClosed={(d) => { setDay(d); setClosing(false); setTab('modes'); }} />
      )}

      {decision && (
        <>
          <div className="modal-backdrop" style={{ zIndex: 10050 }} />
          <div className="modal-container" style={{ zIndex: 10051, maxWidth: 480 }}>
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{decision === 'verify' ? 'Verify the day' : 'Reopen the day'} — {formatDate(date)}</h2>
              <button className="modal-close" onClick={() => setDecision(null)} disabled={deciding}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <p style={{ fontSize: '0.83rem', color: 'var(--text-secondary)', marginTop: 0 }}>
                {decision === 'verify'
                  ? `Confirm the Main Cashier received ${formatCurrency(day?.handedOverAmount ?? 0)} and the close is in order.`
                  : "Reopening unlocks the day's Front Office receipts for correction; it must be closed again."}
              </p>
              <div className="form-group">
                <label className="form-label">{decision === 'verify' ? 'Remarks' : <>Reason <span className="text-danger">*</span></>}</label>
                <input type="text" className="form-input" maxLength={500} value={decisionText} autoFocus onChange={(e) => setDecisionText(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setDecision(null)} disabled={deciding}>Cancel</button>
              <button className={`btn ${decision === 'verify' ? 'btn-primary' : 'btn-warning'}`} onClick={() => { void decide(); }}
                disabled={deciding || (decision === 'reopen' && decisionText.trim().length < 3)}>
                {deciding ? 'Saving…' : decision === 'verify' ? 'Verify' : 'Reopen'}
              </button>
            </div>
          </div>
        </>
      )}
    </EnterpriseScreenShell>
  );
};

export default FrontOfficeClosingScreen;
